import { ReactNode } from 'react';
import {
	AlertTriangle,
	ArrowRight,
	Pencil,
	Trash,
	Clock,
	Trash2,
} from 'lucide-react';
import {
	GroupedSchedule,
	TimeSlotSchedule,
} from '../utils/groupAvailableDatesByWeekday';
import { formatTimeSlotLocal } from '../utils/formatTimeSlotLocal';
import {
	CalendarDataProps,
	CreateCalendarContainer,
} from './CreateCalendarContainer';
import { useModal } from '@/hooks';
import {
	useDeleteAvailableDate,
	useUpdateAvailableDate,
} from '@/services/mutations/available-date';
import { Button } from '@/components/ui';

interface DeleteAvailabilityAlertProps {
	message: ReactNode;
	confirmLabel: string;
	cancelLabel?: string;
	onConfirm: () => void;
	onCancel?: () => void;
}

const DeleteAvailabilityAlert = ({
	message,
	confirmLabel,
	cancelLabel,
	onConfirm,
	onCancel,
}: DeleteAvailabilityAlertProps) => (
	<div className="flex flex-col items-center gap-4 text-center">
		<div className="flex flex-col items-center gap-2">
			<div className="flex justify-center text-rose-500">
				<div className="rounded-full bg-rose-50 p-2.5">
					<AlertTriangle size={22} />
				</div>
			</div>
			<h3 className="text-xs font-black uppercase tracking-wide text-rose-600">
				Atenção
			</h3>
		</div>

		<div className="w-full text-sm leading-relaxed text-stone-600">
			{message}
		</div>

		<div className="flex w-full flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
			{cancelLabel && onCancel && (
				<Button
					type="button"
					variant="text"
					onClick={onCancel}
					text={cancelLabel}
					className="w-full justify-center sm:w-auto"
				/>
			)}
			<Button
				type="button"
				endIcon={
					<ArrowRight className="size-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
				}
				className="group inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-stone-900 px-4 text-xs font-black uppercase tracking-wide text-white shadow-sm hover:bg-stone-800 sm:w-auto sm:min-w-[9.5rem]"
				onClick={onConfirm}
				text={confirmLabel}
			/>
		</div>
	</div>
);

interface CalendarSlotsCardProps {
	groupedSchedule: GroupedSchedule;
	onDelete: (deletedIds: string[]) => void;
}

const CalendarSlotsCard = ({
	groupedSchedule,
	onDelete,
}: CalendarSlotsCardProps) => {
	const { openModal, closeModal } = useModal();

	const openDeleteModal = (content: ReactNode) => {
		openModal({ content, size: 'compact' });
	};

	const openEditModal = (content: ReactNode, title: ReactNode) => {
		openModal({ content, title, size: 'default' });
	};

	const labelMap = {
		0: 'DOMINGO',
		1: 'SEGUNDA',
		2: 'TERÇA',
		3: 'QUARTA',
		4: 'QUINTA',
		5: 'SEXTA',
		6: 'SÁBADO',
	} as const;

	const { mutate: updateMutate } = useUpdateAvailableDate(() => {
		closeModal();
	});

	const { mutate: deleteMutate } = useDeleteAvailableDate((deletedId) => {
		onDelete([deletedId]);
	});

	// --- LÓGICA DE EDITAR ---
	const handleOnEdit = (dayWeek: number, slot: TimeSlotSchedule) => {
		const onEdit = ({ selectedDayWeeks, slots }: CalendarDataProps) => {
			const targetDay = selectedDayWeeks[0];
			const targetSlot = slots[0];

			if (
				targetDay === undefined ||
				!targetSlot?.start ||
				!targetSlot?.end
			)
				return;

			updateMutate({
				id: slot.original_id,
				week_day: targetDay,
				start_time: targetSlot.start.value,
				end_time: targetSlot.end.value,
			});
		};

		openEditModal(
			<CreateCalendarContainer
				onAction={onEdit}
				onCancel={closeModal}
				defaultData={{
					selectedDayWeek: dayWeek,
					selectedFinalTime: slot.end_time,
					selectedInitalTime: slot.start_time,
				}}
				isEditMode
			/>,
			<h3 className="text-lg font-medium text-dark">
				Editar disponibilidade
			</h3>,
		);
	};

	// --- LÓGICA DE DELETAR UM ---
	const handleOnDelete = (dayWeek: number, slot: TimeSlotSchedule) => {
		const message = (
			<div className="space-y-3 text-left text-stone-900">
				<p className="text-center text-base font-bold">
					Tem certeza que deseja deletar a disponibilidade?
				</p>
				<div className="rounded-lg border border-stone-100 bg-stone-50 p-3 text-sm">
					<p>
						Dia da semana:{' '}
						<span className="font-bold">
							{labelMap[dayWeek as keyof typeof labelMap]}
						</span>
					</p>
					<p>
						Horário:{' '}
						<span className="font-bold text-red-600">
							{formatTimeSlotLocal(slot.start_time)} até{' '}
							{formatTimeSlotLocal(slot.end_time)}
						</span>
					</p>
				</div>
			</div>
		);

		openDeleteModal(
			<DeleteAvailabilityAlert
				message={message}
				confirmLabel="Confirmar"
				cancelLabel="Cancelar"
				onCancel={closeModal}
				onConfirm={() => {
					deleteMutate(slot.original_id);
					closeModal();
				}}
			/>,
		);
	};

	// --- LÓGICA DE DELETAR TODOS DO DIA ---
	const handleDeleteAllDay = () => {
		const totalSlots = groupedSchedule.time_slots.length;
		const isSingle = totalSlots === 1;

		const message = (
			<div className="space-y-2 text-stone-900">
				<p className="text-sm font-bold leading-snug">
					{isSingle
						? 'Tem certeza que deseja excluir este horário?'
						: `Excluir todos os horários de ${
								labelMap[
									groupedSchedule.week_day as keyof typeof labelMap
								]
						  }?`}
				</p>
				<p className="text-xs text-stone-500">
					Esta ação removerá{' '}
					<span className="font-bold text-red-600">{totalSlots}</span>{' '}
					{totalSlots === 1
						? 'intervalo de tempo deste dia.'
						: 'intervalos de tempo deste dia.'}
				</p>
			</div>
		);

		openDeleteModal(
			<DeleteAvailabilityAlert
				message={message}
				confirmLabel={isSingle ? 'Confirmar exclusão' : 'Excluir todos'}
				onConfirm={() => {
					groupedSchedule.time_slots.forEach((slot) => {
						deleteMutate(slot.original_id);
					});
					closeModal();
				}}
			/>,
		);
	};

	return (
		<div className="animate-in fade-in w-full space-y-4 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm duration-500 md:w-fit md:min-w-[320px]">
			{/* Header */}
			<div className="flex items-center justify-between pb-1">
				<div className="flex items-center gap-3">
					<div className="flex items-center justify-center rounded-full bg-stone-100 p-1.5">
						<Clock className="size-5 stroke-[2] text-stone-950" />
					</div>
					<h6 className="text-sm font-bold uppercase tracking-wide text-stone-950">
						{
							labelMap[
								groupedSchedule.week_day as keyof typeof labelMap
							]
						}
					</h6>
				</div>

				<button
					onClick={handleDeleteAllDay}
					className="rounded-lg p-2 text-stone-300 transition-all hover:bg-red-50 hover:text-red-500"
					title="Excluir todos os horários"
				>
					<Trash2 className="size-5" />
				</button>
			</div>

			{/* Lista de Horários */}
			<div className="space-y-2">
				{groupedSchedule.time_slots.map((slot, index) => (
					<div
						key={index}
						className="group flex items-center justify-between rounded-lg border border-transparent bg-stone-50 p-3 transition-colors hover:bg-stone-100"
					>
						{/* Texto do Horário */}
						<div className="flex items-baseline gap-2">
							<span className="text-sm font-semibold tracking-tight text-stone-800">
								{formatTimeSlotLocal(slot.start_time)}
							</span>
							<span className="text-xs text-stone-300">até</span>
							<span className="text-sm font-semibold tracking-tight text-stone-800">
								{formatTimeSlotLocal(slot.end_time)}
							</span>
						</div>

						{/* Ações Individuais */}
						<div className="flex items-center gap-1 opacity-60 transition-opacity group-hover:opacity-100">
							<button
								type="button"
								className="rounded-md p-1.5 text-stone-500 transition-all hover:bg-white hover:text-stone-950 hover:shadow-sm"
								onClick={(event) => {
									event.stopPropagation();
									handleOnEdit(
										groupedSchedule.week_day,
										slot,
									);
								}}
								title="Editar"
							>
								<Pencil className="size-3.5" />
							</button>
							<button
								className="rounded-md p-1.5 text-stone-500 transition-all hover:bg-white hover:text-red-600 hover:shadow-sm"
								onClick={() =>
									handleOnDelete(
										groupedSchedule.week_day,
										slot,
									)
								}
								title="Excluir"
							>
								<Trash className="size-3.5" />
							</button>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export { CalendarSlotsCard };
